import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setTenantHeader, API } from '../lib/api';
import { Store, Shield, Eye, EyeOff, Mail, Lock, User, AlertCircle, ArrowRight, Loader2, Sparkles, MessageCircle, Coins } from 'lucide-react';
import axios from 'axios';
import { TenantManifest } from '../components/TenantManifest';

export const Login = () => {
  const { slug } = useParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(!!slug);
  // iter77ct.26: branding da plataforma (nome + logo 8IP) — via GET /platform/branding
  const [platform, setPlatform] = useState({ name: '8IP', tagline: '', logo_url: '' });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Carrega branding da plataforma (usa fetch nativo para evitar interceptors)
    // iter77ct.26: axios interceptor pode redirecionar por 401 refresh — usamos fetch limpo
    fetch(`${API}/platform/branding`).then(r => r.ok ? r.json() : null).then(d => { if (d) setPlatform(d); }).catch(() => {});
    if (slug) resolveTenant();
  }, [slug]);

  const resolveTenant = async () => {
    try {
      const { data } = await axios.get(`${API}/tenant/resolve?subdomain=${slug}`);
      setTenantInfo(data);
      setTenantHeader(data.id);
    } catch { setError('Loja nao encontrada'); }
    finally { setTenantLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = isLogin ? await login(email, password) : await register(email, password, name);
    setLoading(false);
    if (result.success) {
      if (result.user?.role === 'superadmin') navigate('/superadmin');
      else if (['admin', 'operator', 'viewer'].includes(result.user?.role) && slug) navigate(`/${slug}/dashboard`);
      else if (result.user?.role === 'admin') navigate('/');
      else navigate(slug ? `/${slug}/catalogo` : '/');
    } else { setError(result.error); }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0f23]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-violet-400 animate-spin mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  const isTenantLogin = !!slug;
  const resolvedLogoUrl = (url) => url ? (url.startsWith('http') ? url : `${API.replace(/\/api$/, '')}${url}`) : '';

  return (
    <div className="min-h-screen bg-[#0f0f23] text-white overflow-hidden relative" data-testid="login-page">
      <TenantManifest />

      {/* Platform brand — TOP LEFT: logo + nome (sem tagline para uma tela mais limpa) */}
      <div className="absolute top-5 left-5 md:top-7 md:left-8 z-20 flex items-center gap-3" data-testid="platform-brand">
        {platform.logo_url ? (
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden shadow-lg shadow-violet-500/20 ring-1 ring-violet-500/25 flex items-center justify-center">
            <img src={resolvedLogoUrl(platform.logo_url)} alt={platform.name} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Shield size={22} className="text-white" />
          </div>
        )}
        <div className="leading-none">
          <div className="text-white font-black text-2xl md:text-3xl tracking-tight">{platform.name}</div>
          {platform.tagline && <div className="text-violet-300 text-[11px] font-semibold uppercase tracking-wider mt-1">{platform.tagline}</div>}
        </div>
      </div>

      {/* Decorative gradient orbs — violet/indigo palette (matches internal app palette) */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[520px] h-[520px] rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 opacity-[0.18] blur-[120px]" />
        <div className="absolute bottom-[-25%] right-[-15%] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-indigo-600 to-violet-500 opacity-[0.14] blur-[140px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.10),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2 max-w-7xl mx-auto">
        {/* LEFT: Feature Panel — enxuto, sem redundancias com o brand */}
        <div className="hidden lg:flex flex-col justify-center p-12 xl:p-16 pt-32">
          <div className="space-y-9 max-w-md">
            <div>
              <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight text-white">
                Gestao completa,<br />
                <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">turbinada por IA.</span>
              </h2>
              <p className="text-slate-400 text-base mt-5 leading-relaxed">PDV, estoque, financeiro e fiscal integrados — com IA, CRM e Cashback nativos.</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: Sparkles, label: 'IA', badge: true },
                { icon: MessageCircle, label: 'CRM', badge: false },
                { icon: Coins, label: 'Cashback', badge: false },
              ].map((f) => (
                <div key={f.label} className="relative rounded-xl border border-violet-500/20 bg-white/[0.02] backdrop-blur p-4 hover:bg-violet-500/[0.08] hover:border-violet-500/40 transition-all text-center">
                  {f.badge && (
                    <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-fuchsia-500 to-violet-500 text-[8px] font-black text-white tracking-wider shadow-lg shadow-fuchsia-500/30">NOVO</span>
                  )}
                  <f.icon size={20} className="text-violet-400 mx-auto mb-2" />
                  <div className="text-sm font-black text-white">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-slate-600 mt-16">&copy; {new Date().getFullYear()} {platform.name}. Todos os direitos reservados.</p>
        </div>

        {/* RIGHT: Auth Card (com logo do TENANT dentro do card) */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-16 pt-24 lg:pt-16">
          <div className="w-full max-w-md">
            <div className="rounded-3xl border border-violet-500/15 bg-white/[0.03] backdrop-blur-xl shadow-2xl shadow-black/40 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-violet-500 to-indigo-600" />
              <div className="p-8 sm:p-10">
                {/* TENANT logo (mantido dentro do card, como estava antes) */}
                <div className="flex flex-col items-center mb-7">
                  {isTenantLogin && tenantInfo?.logo_url ? (
                    <div className="w-20 h-20 rounded-2xl bg-white p-2.5 shadow-xl border border-white/10 flex items-center justify-center mb-3" data-testid="login-company-logo">
                      <img src={resolvedLogoUrl(tenantInfo.logo_url)} alt={tenantInfo.company_name} className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-violet-500/25 mb-3">
                      {isTenantLogin ? <Store size={26} className="text-white" /> : <Shield size={26} className="text-white" />}
                    </div>
                  )}
                  <h1 className="text-xl font-black text-white tracking-tight">{isTenantLogin ? (tenantInfo?.company_name || slug) : 'Painel Administrativo'}</h1>
                  <p className="text-slate-400 text-xs mt-1">{isLogin ? 'Entre com suas credenciais' : 'Crie sua conta'}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
                  {isTenantLogin && tenantInfo?.is_blocked && (
                    <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/40 text-amber-200 px-4 py-3.5 rounded-xl text-sm" data-testid="tenant-blocked-banner" role="alert">
                      <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-amber-400" />
                      <div>
                        <p className="font-black">Acesso suspenso por pendência financeira</p>
                        <p className="text-amber-200/80 text-xs mt-1">Identificamos uma pendência na mensalidade desta empresa. Entre em contato com a 8IP Tecnologia para regularizar e liberar o acesso.</p>
                      </div>
                    </div>
                  )}
                  {!isLogin && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Nome completo</label>
                      <div className="relative">
                        <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:bg-white/[0.06] transition-all"
                          required={!isLogin} data-testid="name-input" placeholder="Seu nome" autoComplete="name"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">E-mail</label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:bg-white/[0.06] transition-all"
                        required data-testid="email-input" placeholder="seu@email.com" autoComplete="email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 mb-1.5 uppercase tracking-wider">Senha</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-11 py-3 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:bg-white/[0.06] transition-all"
                        required data-testid="password-input" placeholder="Sua senha" autoComplete={isLogin ? 'current-password' : 'new-password'}
                      />
                      <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-violet-300 p-1" data-testid="toggle-password-visibility" aria-label="Mostrar/ocultar senha">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-start gap-2.5 bg-rose-500/10 border border-rose-500/30 text-rose-300 px-4 py-3 rounded-xl text-sm" data-testid="error-message" role="alert">
                      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="submit-button"
                    className="group relative w-full py-3.5 rounded-xl font-black text-white text-sm tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 hover:shadow-xl hover:shadow-violet-500/25 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 shadow-lg shadow-violet-500/20"
                  >
                    {loading ? (<><Loader2 size={17} className="animate-spin" /><span>Aguarde...</span></>) : (<><span>{isLogin ? 'Entrar' : 'Criar conta'}</span><ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>)}
                  </button>
                </form>

                {!isTenantLogin && (
                  <div className="mt-6 text-center">
                    <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-slate-400 hover:text-violet-300 text-sm font-medium transition-colors" data-testid="toggle-auth-mode">
                      {isLogin ? 'Nao tem conta? Cadastre-se' : 'Ja tem conta? Faca login'}
                    </button>
                  </div>
                )}

                <div className="mt-5 pt-5 border-t border-white/[0.06] text-center">
                  <Link to={slug ? `/${slug}/catalogo` : '/'} className="text-slate-500 hover:text-violet-300 text-xs font-medium transition-colors inline-flex items-center gap-1" data-testid="back-link">
                    <ArrowRight size={11} className="rotate-180" />
                    <span>{slug ? `Voltar ao catalogo ${tenantInfo?.company_name || ''}` : 'Voltar a pagina inicial'}</span>
                  </Link>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-slate-600 mt-6 lg:hidden">&copy; {new Date().getFullYear()} {platform.name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
